import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Section = ({ n, title, children }) => (
  <div className="border-t border-brand-border py-8 md:py-10">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-4">
        <div className="text-xs font-display font-semibold uppercase tracking-[0.2em] text-brand-red">
          Section {n}
        </div>
        <div className="font-display font-bold text-brand-ink text-xl md:text-2xl mt-2 leading-tight">
          {title}
        </div>
      </div>
      <div className="md:col-span-8 text-brand-slate leading-relaxed space-y-4">
        {children}
      </div>
    </div>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2 marker:text-brand-red">
    {items.map((it) => (
      <li key={it}>{it}</li>
    ))}
  </ul>
);

const Privacy = () => {
  return (
    <main className="bg-white text-brand-ink min-h-screen">
      <Header />
      <div className="pt-32 md:pt-36 pb-20 px-5 md:px-10 lg:px-16">
        <Link to="/" className="inline-flex items-center gap-2 text-brand-slate hover:text-brand-blue font-display font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="mt-8 max-w-5xl">
          <div className="text-xs md:text-sm font-display font-semibold tracking-[0.2em] uppercase text-brand-red mb-3">
            Legal
          </div>
          <h1 className="section-heading font-display font-bold text-4xl md:text-6xl leading-tight text-brand-ink">
            Privacy Policy
          </h1>
          <p className="mt-6 text-sm text-brand-muted">
            Last Updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </p>
          <p className="mt-6 text-base md:text-lg text-brand-slate max-w-3xl leading-relaxed">
            SNV Elite Solutions (&ldquo;Company,&rdquo; &ldquo;SNV Elite Solutions,&rdquo;
            &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your
            privacy and is committed to protecting your personal information.
            This Privacy Policy explains how we collect, use, disclose, store,
            and safeguard your information when you use our website, communicate
            with us, or engage with our recruiting, staffing, executive search,
            and Recruitment Process Outsourcing (RPO) services.
          </p>

          <div className="mt-6 bg-brand-surface border border-brand-border rounded-xl p-6">
            <div className="font-display font-semibold text-brand-ink mb-3">This Policy applies when you:</div>
            <Bullets
              items={[
                "Visit our website",
                "Submit resumes or employment inquiries",
                "Communicate with us via email, phone, or SMS/text message",
                "Apply for employment opportunities",
                "Use our recruiting, staffing, or consulting services",
                "Interact with our forms, communications, or website tools",
              ]}
            />
            <p className="mt-4 text-brand-slate">
              By using our website or services, you consent to the practices described in this Privacy Policy.
            </p>
          </div>
        </div>

        <div className="mt-14 max-w-5xl">
          <Section n="1" title="Information We Collect">
            <p>
              We may collect personal and professional information you voluntarily provide, including:
            </p>
            <Bullets
              items={[
                "Full name",
                "Email address",
                "Phone number",
                "Mailing address",
                "Employment history",
                "Resume or CV information",
                "Professional licenses and certifications",
                "Job title and work history",
                "LinkedIn or professional profile information",
                "Communication preferences",
                "Information submitted through forms, emails, SMS/text messages, or phone calls",
              ]}
            />
            <p>
              We may also collect limited technical information including browser type, IP address, device information, and website activity logs necessary for website functionality and security.
            </p>
          </Section>

          <Section n="2" title="Sources of Information">
            <p>SNV Elite Solutions may collect information from:</p>
            <Bullets
              items={[
                "Direct candidate or client submissions",
                "LinkedIn and professional networking platforms",
                "Public professional profiles",
                "Job boards and resume databases",
                "Referrals and networking contacts",
                "Recruiting and sourcing platforms",
                "Website forms and inquiries",
                "Email, phone, and SMS/text communications",
                "Third-party service providers and business tools",
              ]}
            />
          </Section>

          <Section n="3" title="Cookies & Tracking Technologies">
            <p>Our website may use basic cookies, analytics tools, and technical tracking technologies necessary for:</p>
            <Bullets
              items={[
                "Website functionality",
                "Performance monitoring",
                "Security and spam prevention",
                "Analytics and website improvement",
              ]}
            />
            <p>Third-party tools or providers may include:</p>
            <Bullets
              items={[
                "Google Analytics",
                "LinkedIn Recruiter",
                "Indeed Recruiter",
                "Website hosting and security providers",
                "Email and SMS delivery providers",
              ]}
            />
            <p>
              We do not currently sell personal information or use behavioral advertising trackers for cross-context behavioral advertising.
            </p>
          </Section>

          <Section n="4" title="How We Use Your Information">
            <p>We may use personal information for legitimate business purposes including:</p>
            <Bullets
              items={[
                "Recruiting and staffing services",
                "Matching candidates with employers",
                "Interview scheduling and coordination",
                "Client and candidate communications",
                "Responding to inquiries",
                "Website administration and security",
                "Legal and regulatory compliance",
                "Business operations and relationship management",
                "Fraud prevention and security monitoring",
              ]}
            />
            <p>We do not sell personal information.</p>
          </Section>

          <Section n="5" title="Email & SMS Communications">
            <p>
              By voluntarily providing your phone number or email address, you consent to receive communications from SNV Elite Solutions regarding:
            </p>
            <Bullets
              items={[
                "Employment opportunities",
                "Recruiting updates",
                "Interview scheduling",
                "Staffing services",
                "Business communications",
                "Follow-up communications",
              ]}
            />
            <p>Message frequency may vary. Message and data rates may apply depending on your mobile carrier and plan.</p>
            <p>You may opt out at any time by:</p>
            <Bullets
              items={[
                "Replying STOP to SMS/text messages",
                "Using unsubscribe links where available",
                "Contacting us directly",
              ]}
            />
            <p>
              For assistance, reply <strong>HELP</strong> or contact us directly.
              Consent to receive communications is not a condition of employment or the purchase of any goods or services.
              We do not share SMS consent data with third parties for marketing or promotional purposes.
            </p>
          </Section>

          <Section n="6" title="SMS Consent & Call-To-Action Disclosure">
            <p>
              I consent to receive SMS text messages and phone calls from SNV Elite Solutions regarding employment opportunities, recruiting updates, interview scheduling, staffing services, and related communications.
            </p>
            <p>
              Message frequency may vary. Message and data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance. Consent is not a condition of employment.
            </p>
            <p>
              By submitting your information, you acknowledge that you have read and agree to our{" "}
              <Link to="/privacy" className="text-brand-red font-semibold link-underline">Privacy Policy</Link>{" "}and{" "}
              <Link to="/terms" className="text-brand-red font-semibold link-underline">Terms &amp; Conditions</Link>.
            </p>
          </Section>

          <Section n="7" title="Third-Party Service Providers">
            <p>
              SNV Elite Solutions may use trusted third-party vendors and service providers to support recruiting, communications, website operations, analytics, hosting, customer relationship management, and business operations.
            </p>
            <p>These providers may include:</p>
            <Bullets
              items={[
                "LinkedIn Recruiter",
                "Indeed Recruiter",
                "Microsoft 365 / Google Workspace",
                "Email and SMS delivery providers",
                "Cloud hosting providers",
                "CRM and ATS systems",
              ]}
            />
            <p>
              Information is shared only as reasonably necessary for legitimate business operations, recruiting services, communications, website functionality, and legal compliance.
            </p>
          </Section>

          <Section n="8" title="Data Retention">
            <p>We retain personal information only as long as reasonably necessary for:</p>
            <Bullets
              items={[
                "Recruiting and staffing operations",
                "Business communications",
                "Legal and regulatory compliance",
                "Recordkeeping and dispute resolution",
              ]}
            />
            <p>When information is no longer needed, it is securely deleted or anonymized where appropriate.</p>
          </Section>

          <Section n="9" title="Data Security">
            <p>
              We implement commercially reasonable administrative, technical, and organizational safeguards designed to protect personal information against unauthorized access, disclosure, alteration, or destruction.
            </p>
            <p>
              However, no method of transmission over the Internet or electronic storage is completely secure, and absolute security cannot be guaranteed.
            </p>
          </Section>

          <Section n="10" title="Your Privacy Rights">
            <p>Depending on your jurisdiction, you may have rights regarding your personal information, including:</p>
            <Bullets
              items={[
                "Requesting access to your information",
                "Requesting correction of inaccurate information",
                "Requesting deletion of certain information",
                "Opting out of certain communications",
                "Requesting information about data usage",
              ]}
            />
            <p>
              To submit a request, please contact us at{" "}
              <a href="mailto:Vishan@Snvelite.com" className="text-brand-red font-semibold link-underline">
                Vishan@Snvelite.com
              </a>.
            </p>
          </Section>

          <Section n="11" title="International Data Transfers">
            <p>
              SNV Elite Solutions operates from India (Udaipur, Rajasthan) and the United States (Dallas, TX). Information may be stored or processed in either jurisdiction, or in other jurisdictions where our service providers operate, and privacy laws may differ from those in your location.
            </p>
            <p>Where required, appropriate safeguards will be implemented for international data transfers.</p>
          </Section>

          <Section n="12" title="Children's Privacy">
            <p>
              Our website and services are intended for adults and professional users. We do not knowingly collect personal information from children under the age of 13.
            </p>
          </Section>

          <Section n="13" title="Updates to This Privacy Policy">
            <p>
              We may update this Privacy Policy periodically. Updates will be reflected by the revised &ldquo;Last Updated&rdquo; date at the top of this page.
            </p>
            <p>Continued use of our website or services after updates constitutes acceptance of the revised policy.</p>
          </Section>

          <Section n="14" title="Company Contact Information">
            <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
              <div className="font-display font-bold text-brand-ink text-lg">SNV Elite Solutions</div>
              <div className="mt-3 text-brand-slate space-y-1">
                <div>India Headquarters — Udaipur, Rajasthan, India</div>
                <div>United States Office — Dallas, TX, United States</div>
                <div>
                  Email:{" "}
                  <a
                    href="mailto:Vishan@Snvelite.com"
                    className="text-brand-red font-semibold link-underline"
                  >
                    Vishan@Snvelite.com
                  </a>
                </div>
                <div>
                  Phone:{" "}
                  <a href="tel:9129152311" className="text-brand-red font-semibold link-underline">
                    912-915-2311
                  </a>
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default Privacy;
